import { api, ApiError } from '../lib/api.js';
import { el, render } from '../lib/dom.js';
import { COUNTRIES } from '../lib/countries.js';
import { NAME_PATTERN_SOURCE, isValidName, NAME_INVALID_MESSAGE } from '../lib/validation.js';
import { pageHeader, card, button, icon, infoList, toast } from '../lib/ui.js';
import { credentialsPanel } from './credentials-panel.js';
import { inviteScheduleControl } from './invite-schedule-fields.js';
import { trialScheduleControl, accessTimezoneSelect, defaultAccessTimezone } from './trial-schedule-fields.js';
import { timezoneLabel } from '../lib/timezone.js';

// ---------------------------------------------------------------------------
// Create organisation.
//
// One submit provisions: Organisation + OrgConfig + the default CORE schedule
// standard + the OWNER user + membership + a join link. The API does that in a
// single transaction, so this form either fully succeeds or changes nothing.
// ---------------------------------------------------------------------------

const req = () => el('span', { class: 'req' }, '*');

export function orgNewView({ navigate, meta }) {
  const container = el('div', { class: 'stack' });

  function form() {
    const error = el('p', { class: 'form-error', role: 'alert', hidden: true });
    const submit = button('Create organisation', {
      variant: 'primary',
      type: 'submit',
      title:
        'Provision the tenant: creates the organisation with a generated CustomerID, its OWNER account, and the ' +
        'default schedule standard. The join link and temporary password are shown once on the next screen and ' +
        'cannot be retrieved again.',
    });

    const fields = {
      name: el('input', { class: 'input', type: 'text', id: 'org-name', required: true, maxlength: '200', placeholder: 'e.g. Acme Construction' }),
      industry: el('input', { class: 'input', type: 'text', id: 'org-industry', maxlength: '100', placeholder: 'Construction' }),
      // A dropdown, not free text: country drives CustomerID generation
      // (CMAI-{country}-{number}) on the backend, so it must always be a real
      // ISO 3166-1 alpha-2 code — see lib/countries.js.
      country: el(
        'select',
        { class: 'input', id: 'org-country', required: true },
        el('option', { value: '', disabled: true, selected: true }, 'Select a country…'),
        COUNTRIES.map((c) => el('option', { value: c.code }, `${c.name} (${c.code})`)),
      ),
      configMode: el('select', { class: 'input', id: 'org-config-mode' }, el('option', { value: 'BASIC' }, 'Basic'), el('option', { value: 'ADVANCED' }, 'Advanced')),
      // Seeds OrgConfig.dataResidency, which the tenant app's Organization
      // Identity tab reads/edits (DATA_REGION_OPTIONS in
      // OrganizationIdentityTab.tsx) — values must stay AU/US to match.
      dataResidency: el(
        'select',
        { class: 'input', id: 'org-data-residency', required: true, onchange: () => onRegionChange() },
        el('option', { value: '', disabled: true, selected: true }, 'Select a data region…'),
        el('option', { value: 'AU' }, 'AU Server'),
        el('option', { value: 'US' }, 'US Server'),
      ),
      // Drives trialLocked, the default trial window and the default limits
      // below (see plan.ts's planWriteData) — this is the source of truth,
      // not whether trial dates happen to be filled in.
      plan: el(
        'select',
        {
          class: 'input',
          id: 'org-plan',
          required: true,
          onchange: () => updatePlanUi(),
        },
        el('option', { value: 'TRIAL', selected: true }, 'Trial'),
        el('option', { value: 'CMAI_CORE' }, 'CMAI Core'),
      ),
      // Required: every Trial / CMAI Core date below is in this timezone (start
      // 00:00, end 23:59). Pre-selected from the data region until the
      // operator picks one themselves; saved as the org's Default timezone.
      accessTimezone: accessTimezoneSelect({ id: 'org-access-timezone', value: defaultAccessTimezone(null, null), onchange: () => { timezoneTouched = true; updatePlanUi(); } }),
      trialStartsAt: el('input', { class: 'input mono', type: 'date', id: 'org-trial-start' }),
      trialEndsAt: el('input', { class: 'input mono', type: 'date', id: 'org-trial-end' }),
      // CMAI Core access window. Picking a start fills the end 12 months later
      // (until the operator edits the end themselves) — see onPlanStartChange.
      planStartsAt: el('input', { class: 'input mono', type: 'date', id: 'org-plan-start', onchange: () => onPlanStartChange() }),
      planEndsAt: el('input', { class: 'input mono', type: 'date', id: 'org-plan-end', oninput: () => { planEndTouched = true; } }),
      maxProjects: el('input', { class: 'input mono', type: 'number', id: 'org-max-projects', min: '0', max: '100000', placeholder: '3 (default)' }),
      revalidationsRemaining: el('input', { class: 'input mono', type: 'number', id: 'org-revalidations-remaining', min: '0', max: '100000', placeholder: '10 (default)' }),
      ownerName: el('input', { class: 'input', type: 'text', id: 'owner-name', required: true, maxlength: '200', pattern: NAME_PATTERN_SOURCE, title: NAME_INVALID_MESSAGE, placeholder: 'e.g. Sarah Chen' }),
      ownerEmail: el('input', { class: 'input', type: 'email', id: 'owner-email', required: true, maxlength: '255', autocomplete: 'off', placeholder: 'owner@client.com' }),
      sendEmail: el('input', {
        type: 'checkbox',
        id: 'send-email',
        checked: meta?.emailConfigured !== false,
        onchange: (e) => invite.setEnabled(e.target.checked),
      }),
    };

    const PLAN_DEFAULTS = {
      TRIAL: { maxProjects: 3, revalidationsRemaining: 10 },
      CMAI_CORE: { maxProjects: 10, revalidationsRemaining: 10 },
    };

    const trialFieldsWrap = el(
      'div',
      { class: 'stack', style: 'gap:12px' },
      el(
        'div',
        { class: 'field-grid' },
        el('div', { class: 'field' }, el('label', { for: 'org-trial-start' }, 'Trial start'), fields.trialStartsAt),
        el('div', { class: 'field' }, el('label', { for: 'org-trial-end' }, 'Trial end'), fields.trialEndsAt),
      ),
      el(
        'p',
        { class: 'field-hint' },
        'Leave both blank to start a 15-day trial right now, or set both to use a different window — from 00:00 on the start date to 23:59 on the end date, in the timezone above. Members can’t sign in before the start, and are locked out after the end unless it is moved. The window is shown in the owner’s welcome email.',
      ),
    );
    const limitsHint = el('p', { class: 'field-hint' });
    const timezoneHint = el('p', { class: 'field-hint' });
    let timezoneTouched = false;
    function onRegionChange() {
      if (!timezoneTouched) fields.accessTimezone.value = defaultAccessTimezone(null, fields.dataResidency.value);
      updatePlanUi();
    }

    // CMAI Core's access window — same months/clamp rule as plan.ts's
    // addMonthsUtc, so the default shown here is exactly what the API stores.
    const CORE_ACCESS_MONTHS = 12;
    let planEndTouched = false;
    const plusMonths = (isoDate, months) => {
      const [y, m, d] = isoDate.split('-').map(Number);
      const out = new Date(Date.UTC(y, m - 1 + months, d));
      if (out.getUTCDate() !== d) out.setUTCDate(0);
      return out.toISOString().slice(0, 10);
    };
    const minusOneDay = (isoDate) => {
      const d = new Date(`${isoDate}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() - 1);
      return d.toISOString().slice(0, 10);
    };
    // "Today" in the selected access timezone, not the browser's.
    const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: fields.accessTimezone.value || undefined });
    const fmtDay = (isoDate) =>
      new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    function onPlanStartChange() {
      if (fields.planStartsAt.value && (!planEndTouched || !fields.planEndsAt.value)) {
        // The end date is inclusive (access until 23:59), so 12 months from
        // 2 Oct ends on 1 Oct the following year.
        fields.planEndsAt.value = minusOneDay(plusMonths(fields.planStartsAt.value, CORE_ACCESS_MONTHS));
        planEndTouched = false;
      }
    }
    const planHint = el('p', { class: 'field-hint' });
    const planFieldsWrap = el(
      'div',
      { class: 'stack', style: 'gap:12px', hidden: true },
      el(
        'div',
        { class: 'field-grid' },
        el('div', { class: 'field' }, el('label', { for: 'org-plan-start' }, 'CMAI Core start'), fields.planStartsAt),
        el('div', { class: 'field' }, el('label', { for: 'org-plan-end' }, 'CMAI Core end'), fields.planEndsAt),
      ),
      planHint,
    );

    function updatePlanUi() {
      const isTrial = fields.plan.value === 'TRIAL';
      trialFieldsWrap.hidden = !isTrial;
      planFieldsWrap.hidden = isTrial;
      const today = todayIso();
      planHint.textContent =
        `Default: ${CORE_ACCESS_MONTHS} months starting right now (${fmtDay(today)} – ${fmtDay(plusMonths(today, CORE_ACCESS_MONTHS))}) — leave both blank to use it. ` +
        'Picking a start fills the end 12 months later. Dates run from 00:00 on the start date to 23:59 on the end date, in the timezone above. Members can’t sign in before the start, and are locked out after the end unless it is moved.';
      timezoneHint.textContent = `All dates below are in ${timezoneLabel(fields.accessTimezone.value)}. It is saved as the organisation’s default timezone, and every screen and email shows these dates in it.`;
      trial.root.hidden = !isTrial;
      const d = PLAN_DEFAULTS[fields.plan.value] ?? PLAN_DEFAULTS.TRIAL;
      fields.maxProjects.placeholder = `${d.maxProjects} (default)`;
      fields.revalidationsRemaining.placeholder = `${d.revalidationsRemaining} (default)`;
      limitsHint.textContent = `Leave blank to use this plan's defaults (${d.maxProjects} projects, ${d.revalidationsRemaining} re-validations).`;
    }

    const trial = trialScheduleControl();
    // The invitation timezone defaults to the organisation's access timezone, once, when "Schedule invitation" is
    // turned on — still freely overridable afterwards.
    const invite = inviteScheduleControl({ getPreferredTimezone: () => fields.accessTimezone.value });
    invite.setEnabled(fields.sendEmail.checked);

    const fail = (message) => {
      error.textContent = message;
      error.hidden = false;
      error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const formEl = el(
      'form',
      {
        class: 'form-card-stack',
        onsubmit: async (e) => {
          e.preventDefault();
          error.hidden = true;

          if (fields.country.value === '') return fail('Select a country.');
          if (fields.dataResidency.value === '') return fail('Select a data residency region.');
          if (!fields.accessTimezone.value) return fail('Select the timezone the plan dates are in.');
          if (!isValidName(fields.ownerName.value)) return fail(NAME_INVALID_MESSAGE);

          // The trial calendar window only applies to the Trial plan — inert
          // (and hidden) on CMAI Core, so never send it for that plan even if
          // a value lingers from before the operator switched plans.
          const isTrial = fields.plan.value === 'TRIAL';
          const trialStartsAt = isTrial ? fields.trialStartsAt.value || undefined : undefined;
          const trialEndsAt = isTrial ? fields.trialEndsAt.value || undefined : undefined;
          if ((trialStartsAt === undefined) !== (trialEndsAt === undefined)) {
            return fail('Set both a trial start and end date, or leave both blank.');
          }
          // Same rule for the CMAI Core window — only sent on CMAI Core.
          const planStartsAt = isTrial ? undefined : fields.planStartsAt.value || undefined;
          const planEndsAt = isTrial ? undefined : fields.planEndsAt.value || undefined;
          if ((planStartsAt === undefined) !== (planEndsAt === undefined)) {
            return fail('Set both a CMAI Core start and end date, or leave both blank for the 12-month default.');
          }
          if (planStartsAt !== undefined && planEndsAt < planStartsAt) {
            return fail('The CMAI Core end date can’t be before its start date.');
          }

          // Both optional — blank means "use the selected plan's default".
          const maxProjectsRaw = fields.maxProjects.value.trim();
          const revalidationsRemainingRaw = fields.revalidationsRemaining.value.trim();
          const maxProjects = maxProjectsRaw === '' ? undefined : Number(maxProjectsRaw);
          const revalidationsRemaining = revalidationsRemainingRaw === '' ? undefined : Number(revalidationsRemainingRaw);
          if (
            (maxProjects !== undefined && (!Number.isInteger(maxProjects) || maxProjects < 0)) ||
            (revalidationsRemaining !== undefined && (!Number.isInteger(revalidationsRemaining) || revalidationsRemaining < 0))
          ) {
            return fail('Max projects and re-validations must be whole numbers of zero or more, or left blank.');
          }

          submit.disabled = true;
          submit.textContent = 'Creating…';
          try {
            const result = await api.createOrg({
              name: fields.name.value.trim(),
              industry: fields.industry.value.trim() || undefined,
              country: fields.country.value,
              configMode: fields.configMode.value,
              dataResidency: fields.dataResidency.value,
              plan: fields.plan.value,
              accessTimezone: fields.accessTimezone.value,
              trialStartsAt,
              trialEndsAt,
              planStartsAt,
              planEndsAt,
              ...(isTrial ? trial.getValue() : {}),
              maxProjects,
              revalidationsRemaining,
              owner: {
                fullName: fields.ownerName.value.trim(),
                email: fields.ownerEmail.value.trim().toLowerCase(),
              },
              sendInviteEmail: fields.sendEmail.checked,
              ...invite.getValue(),
            });

            toast(`${result.organisation.name} created.`);
            render(
              container,
              pageHeader({ title: 'Organisation created', subtitle: 'Copy anything you need below — the join link and temporary password are shown only once.' }),
              credentialsPanel({
                title: `${result.organisation.name} created`,
                user: result.owner,
                customerId: result.organisation.customerId,
                scheduleStandard: result.scheduleStandard,
                email: result.email,
                scheduledInvitation: result.scheduledInvitation,
                warnings: result.warnings,
              }),
              el(
                'div',
                { class: 'panel-actions' },
                button('Open organisation', {
                  variant: 'primary',
                  title: 'Go to the new organisation to add more members or adjust its trial and limits. Copy anything you still need from this panel first.',
                  onClick: () => navigate(`/orgs/${result.organisation.id}`),
                }),
                button('Back to list', {
                  title: 'Return to the organisations list. Copy anything you still need above first — the join link and temporary password are not shown again.',
                  onClick: () => navigate('/orgs'),
                }),
              ),
            );
          } catch (err) {
            let message = err instanceof ApiError ? err.message : 'Failed to create the organisation.';
            if (err instanceof ApiError && Array.isArray(err.details)) {
              message += ` (${err.details.map((d) => `${d.field}: ${d.message}`).join('; ')})`;
            }
            fail(message);
            submit.disabled = false;
            submit.textContent = 'Create organisation';
          }
        },
      },
      card({
        title: 'Organisation',
        subtitle: 'Who the customer is. Country and data residency cannot be changed later from this portal.',
        body: el(
          'div',
          { class: 'form-section' },
          el('div', { class: 'field' }, el('label', { for: 'org-name' }, 'Organisation name ', req()), fields.name),
          el(
            'div',
            { class: 'field-grid' },
            el('div', { class: 'field' }, el('label', { for: 'org-industry' }, 'Industry'), fields.industry),
            el('div', { class: 'field' }, el('label', { for: 'org-country' }, 'Country ', req()), fields.country),
            el('div', { class: 'field' }, el('label', { for: 'org-config-mode' }, 'Config mode'), fields.configMode),
            el('div', { class: 'field' }, el('label', { for: 'org-data-residency' }, 'Data residency ', req()), fields.dataResidency),
          ),
        ),
      }),
      card({
        title: 'Plan & limits',
        subtitle: 'Plan is required — it can be switched later from the organisation’s Trial & settings tab. The individual limits below are optional overrides.',
        body: el(
          'div',
          { class: 'form-section' },
          el('div', { class: 'field' }, el('label', { for: 'org-plan' }, 'Plan ', req()), fields.plan),
          el('div', { class: 'field' }, el('label', { for: 'org-access-timezone' }, 'Timezone ', req()), fields.accessTimezone, timezoneHint),
          trialFieldsWrap,
          trial.root,
          planFieldsWrap,
          el(
            'div',
            { class: 'field-grid' },
            el('div', { class: 'field' }, el('label', { for: 'org-max-projects' }, 'Max projects'), fields.maxProjects),
            el('div', { class: 'field' }, el('label', { for: 'org-revalidations-remaining' }, 'Re-validations remaining'), fields.revalidationsRemaining),
          ),
          limitsHint,
        ),
      }),
      card({
        title: 'Owner & invitation',
        subtitle: 'The first OWNER of this organisation.',
        body: el(
          'div',
          { class: 'form-section' },
          el(
            'p',
            { class: 'field-hint' },
            'An existing ConstructMind user can be the owner — they keep their password and gain this organisation as well.',
          ),
          el(
            'div',
            { class: 'field-grid' },
            el('div', { class: 'field' }, el('label', { for: 'owner-name' }, 'Full name ', req()), fields.ownerName),
            el('div', { class: 'field' }, el('label', { for: 'owner-email' }, 'Email ', req()), fields.ownerEmail),
          ),
          el('label', { class: 'checkbox-field', for: 'send-email' }, fields.sendEmail, el('span', { class: 'box' }), 'Send the invite email'),
          meta?.emailConfigured === false
            ? el('p', { class: 'note note-warn' }, 'Email is not configured on this deployment. Nothing will be sent — you will be given the join link to deliver manually.')
            : null,
          invite.root,
        ),
      }),
      error,
      el(
        'div',
        { class: 'form-actions' },
        button('Cancel', { title: 'Discard this form and return to the organisations list. Nothing is created.', onClick: () => navigate('/orgs') }),
        submit,
      ),
    );

    updatePlanUi();
    requestAnimationFrame(() => fields.name.focus());
    return formEl;
  }

  const summary = card({
    title: 'What gets created',
    className: 'form-aside',
    body: el(
      'div',
      { class: 'stack', style: 'gap:12px' },
      el('p', { class: 'section-hint' }, 'One submit provisions everything below in a single transaction — it either fully succeeds or changes nothing.'),
      infoList([
        ['Organisation', 'with a generated Customer ID'],
        ['Org config', 'data residency & governance'],
        ['Schedule standard', `${meta?.defaultScheduleStandard?.title ?? 'Default standard'} · ${meta?.defaultScheduleStandard?.ruleCount ?? '—'} rules`],
        ['Owner account', 'OWNER role + join link'],
        ['Join link expiry', meta?.activationTtlMinutes ? `${Math.round(meta.activationTtlMinutes / 60)} hours` : '—'],
      ]),
    ),
  });

  render(
    container,
    el('button', { type: 'button', class: 'back-link', onclick: () => navigate('/orgs') }, icon('arrow-left'), 'Back to Organisations'),
    pageHeader({ title: 'New organisation', subtitle: 'Provision a customer organisation, its first owner and the default schedule standard.' }),
    el('div', { class: 'form-page' }, form(), summary),
  );

  return container;
}
